import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { config } from "../config";
import { bindAuth } from "../api/client";
import { beginLogin, loadTokens, refresh, saveTokens, type TokenSet } from "./pkce";
import { globalSignOut } from "./cognito";

interface AuthState {
  tokens: TokenSet | null;
  /** A valid ID token for the API, refreshing first if it is about to expire. */
  idToken: () => Promise<string | null>;
  login: (returnTo?: string) => Promise<void>;
  logout: () => void;
  setTokens: (t: TokenSet) => void;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [tokens, setTokensState] = useState<TokenSet | null>(() => {
    const dev = import.meta.env.VITE_DEV_ID_TOKEN;
    if (dev) return { id_token: dev, access_token: dev, expires_at: Date.now() + 3600_000 };
    return loadTokens();
  });
  const setTokens = useCallback((t: TokenSet) => {
    saveTokens(t);
    setTokensState(t);
  }, []);
  const idToken = useCallback(async () => {
    if (!tokens) return null;
    if (tokens.expires_at - Date.now() > 60_000) return tokens.id_token;
    const c = config();
    const next = await refresh(c.cognito_domain, c.cognito_client_id, tokens);
    if (!next) {
      saveTokens(null);
      setTokensState(null);
      return null;
    }
    setTokensState(next);
    return next.id_token;
  }, [tokens]);
  const login = useCallback(async (returnTo?: string) => {
    const c = config();
    if (!c.cognito_domain || !c.cognito_client_id) throw new Error("Cognito is not configured (config.json)");
    await beginLogin(c.cognito_domain, c.cognito_client_id, returnTo ?? window.location.pathname);
  }, []);
  const logout = useCallback(() => {
    // Revoke the refresh token server-side (best effort), forget the session here, and
    // land on our own sign-in page. No round trip through the hosted page.
    const c = config();
    const access = tokens?.access_token;
    saveTokens(null);
    setTokensState(null);
    if (c.cognito_region && access) void globalSignOut(c.cognito_region, access);
    window.location.assign("/");
  }, [tokens]);
  useEffect(() => {
    // Refresh in the background a minute before expiry.
    if (!tokens?.refresh_token) return;
    const wait = Math.max(5_000, tokens.expires_at - Date.now() - 60_000);
    const id = window.setTimeout(() => void idToken(), wait);
    return () => window.clearTimeout(id);
  }, [tokens, idToken]);
  const value = useMemo(() => ({ tokens, idToken, login, logout, setTokens }), [tokens, idToken, login, logout, setTokens]);
  // Bound during render, not in an effect: children's queries fire in their own effects,
  // which run before this component's, and must already carry the bearer.
  bindAuth(idToken, logout);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}
